/**
 * ALDER SEARCH Search Outcomes Section
 * Design: "Alder Forest Organic Precision"
 * Warm stone background, editorial pull quotes
 * Horizontal scrollable on mobile
 */

import { useEffect, useRef, useState } from "react";

const searchOutcomes = [
  {
    title: "Principal Product Manager",
    description: "London",
    detail: "Lansweeper · Technology",
  },
  {
    title: "Principal Product Manager",
    description: "Austin",
    detail: "Domotz · Technology",
  },
  {
    title: "Head of Product",
    description: "London",
    detail: "Nokkel · Fintech",
  },
  {
    title: "Chief Product Officer",
    description: "London",
    detail: "Datapharm · Healthcare",
  },
  {
    title: "VP Product",
    description: "London",
    detail: "Gentrack · Energy & Commodities",
  },
];

export default function TestimonialsSection() {
  const [active, setActive] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      if (contentRef.current) {
        contentRef.current.style.opacity = "1";
        contentRef.current.style.transform = "none";
      }
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && contentRef.current) {
            contentRef.current.style.opacity = "1";
            contentRef.current.style.transform = "translateY(0)";
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    if (contentRef.current) {
      contentRef.current.style.opacity = "0";
      contentRef.current.style.transform = "translateY(24px)";
      contentRef.current.style.transition = "opacity 0.8s ease-out, transform 0.8s ease-out";
    }
    const fallback = setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.style.opacity = "1";
        contentRef.current.style.transform = "translateY(0)";
      }
    }, 2000);
    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  // Auto-advance
  useEffect(() => {
    const interval = setInterval(() => {
      setActive((prev) => (prev + 1) % searchOutcomes.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const outcome = searchOutcomes[active];

  return (
    <section
      ref={sectionRef}
      className="py-24 relative overflow-hidden"
      style={{ background: "oklch(0.245 0.065 155)" }}
    >
      <div className="container relative z-10" ref={contentRef}>
        <div className="max-w-3xl mx-auto text-center">
          {/* Eyebrow */}
          <div className="flex items-center justify-center gap-3 mb-10">
            <div
              className="h-px w-8"
              style={{ background: "oklch(0.62 0.085 75)" }}
            />
            <span
              className="font-ui text-xs tracking-widest uppercase"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                color: "oklch(0.62 0.085 75)",
                letterSpacing: "0.18em",
              }}
            >
              Search Outcomes
            </span>
            <div
              className="h-px w-8"
              style={{ background: "oklch(0.62 0.085 75)" }}
            />
          </div>

          <h2
            className="font-display mb-8"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 600,
              fontSize: "clamp(1.1rem, 2.2vw, 1.5rem)",
              lineHeight: 1.65,
              color: "oklch(0.88 0.015 80)",
              transition: "opacity 0.4s ease",
            }}
            key={active}
          >
            {outcome.title}
          </h2>

          {/* Outcome detail */}
          <div className="flex flex-col items-center gap-1 mb-10">
            <div
              className="h-px w-8 mb-3"
              style={{ background: "oklch(0.62 0.085 75 / 0.5)" }}
            />
            <span
              className="font-ui"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontWeight: 500,
                fontSize: "0.82rem",
                color: "oklch(0.72 0.09 75)",
                letterSpacing: "0.04em",
              }}
            >
              {outcome.description}
            </span>
            <span
              className="font-ui"
              style={{
                fontFamily: "'DM Mono', monospace",
                fontWeight: 300,
                fontSize: "0.68rem",
                color: "oklch(0.55 0.02 155)",
                letterSpacing: "0.06em",
              }}
            >
              {outcome.detail}
            </span>
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-2">
            {searchOutcomes.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className="relative flex h-11 w-11 items-center justify-center transition-all duration-300"
                style={{
                  borderRadius: "999px",
                  background: "transparent",
                  border: "none",
                }}
                aria-label={`View search outcome ${i + 1}`}
              >
                <span
                  aria-hidden="true"
                  className="block transition-all duration-300"
                  style={{
                    width: i === active ? "24px" : "6px",
                    height: "6px",
                    borderRadius: "3px",
                    background:
                      i === active
                        ? "oklch(0.62 0.085 75)"
                        : "oklch(0.38 0.07 155)",
                  }}
                />
              </button>
            ))}
          </div>

          <div
            className="mt-14 p-8 relative text-left"
            style={{
              background: "oklch(0.32 0.07 155)",
              borderRadius: "4px",
              borderLeft: "3px solid oklch(0.62 0.085 75)",
            }}
          >
            <p
              className="font-body mb-4"
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: "1rem",
                lineHeight: 1.75,
                color: "oklch(0.88 0.015 80)",
              }}
            >
              Ryan is a delight to work with, very responsive and finely tuned to the needs of both the candidates and the business talent requirements. I&apos;d highly recommend Ryan for senior product hiring!
            </p>
            <div
              className="font-ui text-xs"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontWeight: 500,
                fontSize: "0.72rem",
                color: "oklch(0.62 0.085 75)",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              Madhu Nori, Interim Chief Product Officer, Datapharm
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
